// Demo mock "backend": resolves API requests against the IndexedDB-backed store.
// Returns { status, data }. Non-2xx is turned into a rejected axios error by the
// adapter so existing UI error handling (toasts etc.) behaves normally.

import { STORES, list, getById, put, remove, getAnalytics, appendAiHistory } from "./store";
import { canCreate, recordCreate, limitMessage } from "./limits";
import { findSeeded, AI_DISCLAIMER } from "./aiResponses";
import { findRuleAnswer, genericAnswer } from "./aiRules";

const ok = (data = {}) => ({ status: 200, data });
const created = (data = {}) => ({ status: 201, data });
const blocked = (msg = "Not available in the demo.") => ({ status: 403, data: { detail: msg } });

function uid(prefix) {
  return `${prefix}-${Math.random().toString(16).slice(2, 10)}`;
}
function nowIso() {
  return new Date().toISOString();
}
function startOfMonth(month) {
  if (!month) return null;
  const value = String(month).slice(0, 7);
  if (!/^\d{4}-\d{2}$/.test(value)) return null;
  return `${value}-01`;
}
function tail(path, prefix) {
  // returns the id segment(s) after `${prefix}/`
  return decodeURIComponent(path.slice(prefix.length + 1));
}
function asArray(value) {
  return Array.isArray(value) ? value : [];
}
async function dashboardTasksForMonth(month) {
  const tasks = await list(STORES.dashboardTasks);
  const normalized = asArray(tasks);
  if (!month) return normalized;
  const monthKey = String(month).slice(0, 7);
  return normalized.filter((task) => String(task?.date || "").slice(0, 7) === monthKey);
}
async function dashboardTasksForDate(date) {
  const tasks = await list(STORES.dashboardTasks);
  const normalized = asArray(tasks);
  if (!date) return normalized;
  const dateKey = String(date).slice(0, 10);
  return normalized.filter((task) => String(task?.date || "").slice(0, 10) === dateKey);
}
async function dashboardCalendarFeed(month) {
  const tasks = await dashboardTasksForMonth(month);
  const dateFallback = startOfMonth(month) || new Date().toISOString().slice(0, 10);
  const events = tasks.map((task, index) => {
    const date = task?.date || dateFallback;
    const title = task?.title || task?.text || "Demo task";
    const start_time = task?.start_time || `${String((index % 8) + 9).padStart(2, "0")}:00`;
    return {
      id: task?.id || uid("event"),
      date,
      start_time,
      end_time: task?.end_time || "",
      title,
      description: task?.text || title,
      event_type: task?.category === "vision" ? "vision" : "task",
      priority: task?.priority || "medium",
      status: task?.status || "pending",
      task_id: task?.id || null,
    };
  });
  return events;
}
async function dashboardNotifications() {
  const tasks = asArray(await list(STORES.dashboardTasks));
  return tasks.slice(0, 6).map((task, index) => ({
    id: `notif-${task?.id || index}`,
    title: task?.status === "done" ? "Task completed" : "Task reminder",
    body: task?.title || task?.text || "Demo dashboard update",
    play_sound: index === 0,
    date: task?.date || new Date().toISOString().slice(0, 10),
    level: task?.priority || "medium",
  }));
}

// ---- the demo "logged in" user ----
// business_id "default" so api.js adds no tenant prefix; section_permissions
// limited to the allowed sections so blocked sections vanish from the nav.
const ALLOWED_SECTIONS = [
  "/",
  "/customers",
  "/items",
  "/invoices",
  "/repairs",
  "/purchases",
  "/reports",
  "/customer-balances",
  "/supplier-balances",
  "/karigars",
];
function demoUser() {
  const section_permissions = {};
  for (const s of ALLOWED_SECTIONS) section_permissions[s] = "view_edit";
  return {
    id: "demo-user",
    external_id: "demo-user",
    email: "demo@jewel-flow.com",
    full_name: "Demo User",
    role: "admin",
    business_id: "default",
    business_name: "JewelFlow Demo Jewellers",
    is_approved: true,
    is_active: true,
    theme: "light",
    allowed_sections: ALLOWED_SECTIONS,
    section_permissions,
    global_allow_screenshots: true,
    global_allow_screen_share: true,
    session_timeout_minutes: 0,
  };
}

// ---- endpoints that must stay blocked in the demo ----
const BLOCKED_PATTERNS = [
  /(^|\/)export(s)?(\/|$)/,
  /(^|\/)import(s)?(\/|$)/,
  /(^|\/)backup(s)?(\/|$)/,
  /(^|\/)restore(\/|$)/,
  /(^|\/)cloud[-_]?sync(\/|$)/,
  /(^|\/)delete-all(\/|$)/,
  /^\/admin\//,
  /^\/local-admin\//,
  /^\/users(\/|$)/,
  /(^|\/)permission(s)?(\/|$)/,
  /(^|\/)ownership(\/|$)/,
  /^\/auth\/(login|register|google)(\/|$)/,
];
function isBlocked(path) {
  return BLOCKED_PATTERNS.some((re) => re.test(path));
}

// ---------------------------------------------------------------- AI
function questionFromBody(body) {
  if (!body || typeof body !== "object") return String(body || "");
  return String(body.message || body.question || body.query || body.text || body.prompt || "");
}
async function handleAi(body) {
  if (!canCreate("ai")) return blocked(limitMessage("ai"));
  recordCreate("ai");
  const question = questionFromBody(body);

  const seeded = findSeeded(question);
  let answer;
  let source;
  if (seeded) {
    answer = seeded;
    source = "seeded";
  } else {
    const [inventory, customers, repairs, orders, invoices, suppliers, purchases, payments, analytics] =
      await Promise.all([
        list(STORES.inventory),
        list(STORES.customers),
        list(STORES.repairs),
        list(STORES.orders),
        list(STORES.estimates),
        list(STORES.suppliers),
        list(STORES.purchases),
        list(STORES.payments),
        getAnalytics(),
      ]);
    const data = { inventory, customers, repairs, orders, invoices, suppliers, purchases, payments, analytics };
    answer = findRuleAnswer(question, data) || genericAnswer(data);
    source = "rule";
  }
  appendAiHistory({ question, answer, source });
  // Provide several common response keys so the assistant UI finds the text.
  return ok({
    answer,
    response: answer,
    message: answer,
    text: answer,
    reply: answer,
    type: "text",
    source,
    demo: true,
    disclaimer: AI_DISCLAIMER,
  });
}

// ---------------------------------------------------------------- dashboard
async function dashboardSummary() {
  const [customers, suppliers, repairs, orders, invoices, gold, analytics] = await Promise.all([
    list(STORES.customers),
    list(STORES.suppliers),
    list(STORES.repairs),
    list(STORES.orders),
    list(STORES.estimates),
    list(STORES.goldRates),
    getAnalytics(),
  ]);
  const pending_cash = customers.reduce((s, c) => s + Number(c.pending_cash || 0), 0);
  const pending_metal = customers.reduce((s, c) => s + Number(c.pending_metal || 0), 0);
  const supplier_pending_cash = suppliers.reduce((s, c) => s + Number(c.pending_cash || 0), 0);
  const supplier_pending_metal = suppliers.reduce((s, c) => s + Number(c.pending_metal || 0), 0);
  const pendingRepairs = repairs.filter((r) => !["collected", "delivered"].includes(r.status)).length;
  const pendingOrders = orders.filter((o) => !["ready", "delivered", "completed"].includes(o.status)).length;
  const rate = gold[0] || {};
  const recent_ledger_activity = invoices.slice(0, 10).map((inv) => ({
    id: inv.id,
    date: inv.invoice_date,
    customer_name: inv.customer_name,
    description: `Estimate ${inv.invoice_number}`,
    amount: inv.grand_total,
    type: "sale",
  }));
  return {
    totals: { pending_cash, pending_metal, supplier_pending_cash, supplier_pending_metal },
    pending: { repairs: pendingRepairs, orders: pendingOrders },
    today_fixed_gold_rate: { rate_per_gram: rate.rate_per_gram, date: rate.date },
    analytics,
    recent_ledger_activity,
    weekly_sales: analytics.weekly_sales || [],
    total_customers: customers.length,
  };
}

async function liveRates() {
  const gold = (await list(STORES.goldRates))[0] || {};
  return {
    success: true,
    gold_usd: 2390.5,
    chennai_24k: gold.rate_24k,
    chennai_22k: gold.rate_22k,
    rate_24k: gold.rate_24k,
    rate_22k: gold.rate_22k,
    silver_per_kg: 92500,
    updated_at: gold.updated_at,
  };
}

// ---------------------------------------------------------------- items
async function itemsBootstrap(params) {
  const items = await list(STORES.inventory);
  const tab = params && params.item_tab;
  const scoped = tab ? items.filter((i) => i.item_tab === tab) : items;
  const total = scoped.length;
  const available = scoped.filter((i) => i.status === "available").length;
  const sold = scoped.filter((i) => i.status === "sold").length;
  const maxTag = items.reduce((m, i) => Math.max(m, Number(i.tag_no) || 0), 1000);
  return { counts: { total, available, sold }, next_tag_no: String(maxTag + 1) };
}

function filterItems(items, params = {}) {
  let rows = items;
  if (params.status) rows = rows.filter((i) => i.status === params.status);
  if (params.item_tab) rows = rows.filter((i) => i.item_tab === params.item_tab);
  if (params.purity) rows = rows.filter((i) => String(i.purity) === String(params.purity));
  if (params.search) {
    const q = String(params.search).toLowerCase();
    rows = rows.filter(
      (i) => String(i.item_name).toLowerCase().includes(q) || String(i.tag_no).includes(q)
    );
  }
  if (params.min_gross !== undefined) rows = rows.filter((i) => Number(i.gross_weight) >= Number(params.min_gross));
  if (params.max_gross !== undefined) rows = rows.filter((i) => Number(i.gross_weight) <= Number(params.max_gross));
  return rows;
}

// ---------------------------------------------------------------- back-office helpers
function round(n, d = 0) {
  const f = 10 ** d;
  return Math.round(Number(n || 0) * f) / f;
}
function withAltId(row) {
  return row && row.id && !row._id ? { ...row, _id: row.id } : row;
}
const RATE_24K = 7320;

// Customer card rows (shape consumed by CustomerBalance pages).
async function customerPendingRows() {
  const customers = await list(STORES.customers);
  return customers
    .filter((c) => Number(c.pending_cash || 0) > 0 || Number(c.pending_metal || 0) > 0)
    .map((c) => ({
      customer: { id: c.id, _id: c.id, name: c.name, whatsapp: c.whatsapp },
      cash: Number(c.pending_cash || 0),
      metal: Number(c.pending_metal || 0),
      storage: 0,
      due_from_date: c.last_billed_date || null,
      overdue_days: 0,
      pending_metal_by_type: null,
      metal_storage_by_type: null,
    }));
}

// Supplier "pending summary" rows (flat shape) + bootstrap (nested shape).
async function supplierPendingSummary() {
  const suppliers = await list(STORES.suppliers);
  return suppliers
    .filter((s) => Number(s.pending_cash || 0) > 0 || Number(s.pending_metal || 0) > 0)
    .map((s) => ({
      supplier_id: s.id,
      name: s.name,
      whatsapp: s.whatsapp,
      pending_cash: Number(s.pending_cash || 0),
      pending_metal: Number(s.pending_metal || 0),
      storage: 0,
    }));
}
async function supplierBootstrapPending() {
  const suppliers = await list(STORES.suppliers);
  return suppliers
    .filter((s) => Number(s.pending_cash || 0) > 0 || Number(s.pending_metal || 0) > 0)
    .map((s) => ({
      supplier: { id: s.id, _id: s.id, name: s.name, whatsapp: s.whatsapp },
      cash: Number(s.pending_cash || 0),
      metal: Number(s.pending_metal || 0),
      storage: 0,
    }));
}

async function customerStatement(customerId) {
  if (!customerId) return null;
  const [customer, invoices, payments, repairs] = await Promise.all([
    getById(STORES.customers, customerId),
    list(STORES.estimates),
    list(STORES.payments),
    list(STORES.repairs),
  ]);
  const c = customer || {};
  const custInvoices = invoices.filter((i) => i.customer_id === customerId && i.doc_type === "invoice");
  const custPays = payments.filter((p) => p.party_type === "customer" && p.party_id === customerId);
  const custRepairs = repairs.filter((r) => r.customer_id === customerId);
  const ledger = [];
  custInvoices.forEach((inv) =>
    ledger.push({
      id: inv.id,
      _id: inv.id,
      date: inv.invoice_date,
      type: "invoice",
      particulars: inv.invoice_number,
      cash_change: Number(inv.grand_total || 0),
      metal_change: Number(inv.total_fine || 0),
      source: "invoice",
    })
  );
  custPays.forEach((p) =>
    ledger.push({
      id: p.id,
      _id: p.id,
      date: p.date,
      type: "payment",
      particulars: p.note || p.ref_no || "Receipt",
      cash_change: Number(p.cash_amount || 0),
      metal_change: Number(p.metal_fine || 0),
      source: "payment",
    })
  );
  ledger.sort((a, b) => new Date(a.date) - new Date(b.date));
  return {
    customer: c,
    totals: {
      pending_cash: Number(c.pending_cash || 0),
      pending_metal: Number(c.pending_metal || 0),
      pending_cash_subtotal: Number(c.pending_cash || 0),
      converted_cash: 0,
      metal_storage_balance: 0,
      metal_storage_by_type: {},
      pending_metal_by_type: {},
    },
    ledger,
    metal_history: [],
    invoices: custInvoices,
    repairs: custRepairs,
  };
}

async function supplierStatement(supplierId) {
  if (!supplierId) return null;
  const [supplier, purchases, payments] = await Promise.all([
    getById(STORES.suppliers, supplierId),
    list(STORES.purchases),
    list(STORES.payments),
  ]);
  const s = supplier || {};
  const supPurchases = purchases.filter((p) => p.supplier_id === supplierId);
  const supPays = payments.filter((p) => p.party_type === "supplier" && p.party_id === supplierId);
  const ledger = [];
  supPurchases.forEach((p) =>
    ledger.push({
      id: p.id,
      _id: p.id,
      date: p.purchase_date,
      type: p.status === "returned" ? "return" : "purchase",
      purchase_type: p.purchase_type,
      cash_change: Number(p.total_amount || 0),
      metal_change: Number(p.fine_weight || 0),
      source: p.status === "returned" ? "return" : "purchase",
    })
  );
  supPays.forEach((p) =>
    ledger.push({
      id: p.id,
      _id: p.id,
      date: p.date,
      type: "payment",
      cash_change: Number(p.cash_amount || 0),
      metal_change: Number(p.metal_fine || 0),
      source: "payment",
    })
  );
  ledger.sort((a, b) => new Date(a.date) - new Date(b.date));
  return {
    supplier: s,
    totals: {
      pending_cash: Number(s.pending_cash || 0),
      pending_metal: Number(s.pending_metal || 0),
      pending_cash_subtotal: Number(s.pending_cash || 0),
      converted_cash: 0,
      metal_storage_balance: 0,
    },
    ledger,
    metal_history: [],
    purchases: supPurchases,
  };
}

async function karigarOverview(karigarId) {
  const [karigar, orders, repairs] = await Promise.all([
    getById(STORES.karigars, karigarId),
    list(STORES.orders),
    list(STORES.repairs),
  ]);
  const k = karigar || {};
  const kOrders = orders.filter((o) => o.karigar_id === karigarId);
  const kRepairs = repairs.filter((r) => r.karigar_id === karigarId);

  // Build ledger events with the EXACT shape the Karigar page renders:
  // every event needs string `type` + `sub_type` (the badge calls
  // `.toUpperCase()` on both) and numeric `fine_in`/`fine_out`. Metal handed to
  // the karigar = fine_out; metal returned on completion = fine_in.
  const raw = [];
  const ORDER_DONE = ["ready", "delivered", "completed"];
  const REPAIR_DONE = ["ready", "collected", "delivered"];
  kOrders.forEach((o) => {
    const fine = round(Number(o.estimate_weight || 0) * 0.916, 3);
    raw.push({
      id: `${o.id}-given`,
      date: o.order_date,
      type: "order",
      sub_type: "given",
      description: `Issued for ${o.item_description || o.item_name || "work order"}`,
      fine_in: 0,
      fine_out: fine,
      status: o.status,
      amount: Number(o.amount || 0),
    });
    if (ORDER_DONE.includes(o.status)) {
      raw.push({
        id: `${o.id}-returned`,
        date: o.expected_completion || o.due_date || o.order_date,
        type: "order",
        sub_type: "returned",
        description: `Returned — ${o.item_description || o.item_name || "work order"}`,
        fine_in: fine,
        fine_out: 0,
        status: o.status,
        amount: Number(o.amount || 0),
      });
    }
  });
  kRepairs.forEach((r) => {
    const fine = round(Number(r.customer_given_weight || 0) * 0.916, 3);
    raw.push({
      id: `${r.id}-given`,
      date: r.estimate_date,
      type: "repair",
      sub_type: "given",
      description: `Repair issued — ${r.item_name || r.description || "repair"}`,
      fine_in: 0,
      fine_out: fine,
      status: r.status,
      amount: Number(r.amount || 0),
    });
    if (REPAIR_DONE.includes(r.status)) {
      raw.push({
        id: `${r.id}-returned`,
        date: r.due_date || r.estimate_date,
        type: "repair",
        sub_type: "returned",
        description: `Repair returned — ${r.item_name || r.description || "repair"}`,
        fine_in: fine,
        fine_out: 0,
        status: r.status,
        amount: Number(r.amount || 0),
      });
    }
  });

  // Running balance (metal still with the karigar) computed oldest → newest,
  // then presented newest-first.
  raw.sort((a, b) => new Date(a.date) - new Date(b.date));
  let bal = 0;
  raw.forEach((e) => {
    bal = round(bal + Number(e.fine_out || 0) - Number(e.fine_in || 0), 3);
    e.balance_after = bal;
  });
  const ledger = raw.reverse();

  return {
    karigar: k,
    metal_summary: {
      total_metal_given_fine: Number(k.total_metal_given_fine || 0),
      total_metal_returned_fine: Number(k.total_metal_returned_fine || 0),
      pending_metal_fine: Number(k.pending_metal_fine || 0),
    },
    ledger,
  };
}

async function consignmentDashboard() {
  const memos = await list(STORES.consignmentMemos);
  const active = memos.filter((m) => m.status !== "settled");
  const active_fine_weight = round(active.reduce((s, m) => s + Number(m.active_fine_weight || 0), 0), 3);
  const sold_from_approval = round(memos.reduce((s, m) => s + Number(m.total_sold_weight || 0), 0), 3);
  const pending_supplier_payable = Math.round(memos.reduce((s, m) => s + Number(m.estimated_payable || 0), 0));
  return {
    active_approval_value: Math.round(active_fine_weight * RATE_24K),
    active_fine_weight,
    sold_from_approval,
    pending_supplier_payable,
    gross_margin_earned: Math.round(pending_supplier_payable * 0.12),
    return_due_soon: memos.filter((m) => m.status !== "settled" && Number(m.days_held || 0) > 30).length,
  };
}

async function supplierExposure() {
  const memos = await list(STORES.consignmentMemos);
  const map = {};
  memos.forEach((m) => {
    if (!map[m.supplier_id]) {
      map[m.supplier_id] = {
        supplier_id: m.supplier_id,
        supplier_name: m.supplier_name,
        name: m.supplier_name,
        active_fine_weight: 0,
        exposure: 0,
        memo_count: 0,
      };
    }
    map[m.supplier_id].active_fine_weight += Number(m.active_fine_weight || 0);
    map[m.supplier_id].exposure += Number(m.estimated_payable || 0);
    map[m.supplier_id].memo_count += 1;
  });
  return Object.values(map).map((r) => ({
    ...r,
    active_fine_weight: round(r.active_fine_weight, 3),
    exposure: Math.round(r.exposure),
  }));
}

async function stockSummary() {
  const items = (await list(STORES.inventory)).filter((i) => i.status === "available");
  const sum = (pred, field) => round(items.filter(pred).reduce((s, i) => s + Number(i[field] || 0), 0), 3);
  return {
    "916_gross": sum((i) => i.purity === "22K", "gross_weight"),
    "916_net": sum((i) => i.purity === "22K", "net_weight"),
    "750_gross": sum((i) => i.purity === "18K", "gross_weight"),
    "750_net": sum((i) => i.purity === "18K", "net_weight"),
  };
}

async function pnlOverview() {
  const items = (await list(STORES.inventory)).filter((i) => i.status === "sold");
  const itemsSold = items.length;
  const totalSaleFine = Math.round(
    items.reduce((s, i) => s + Number(i.net_weight || 0) * ((i.touch || 91.6) / 100) * RATE_24K, 0)
  );
  const totalPurchaseFine = Math.round(totalSaleFine * 0.9);
  const totalSaleStone = Math.round(items.reduce((s, i) => s + Number(i.stone_charges || 0), 0));
  const totalPurchaseStone = Math.round(totalSaleStone * 0.78);
  const metalPl = totalSaleFine - totalPurchaseFine;
  const stonePl = totalSaleStone - totalPurchaseStone;
  return {
    items_sold: itemsSold,
    avg_profit_per_item: itemsSold ? Math.round((metalPl + stonePl) / itemsSold) : 0,
    total_metal_pl: metalPl,
    total_stone_pl: stonePl,
    total_sale_fine_with_cost: totalSaleFine,
    total_sale_stone: totalSaleStone,
    total_purchase_fine: totalPurchaseFine,
    total_purchase_stone: totalPurchaseStone,
    metal_margin_pct: totalSaleFine ? round((metalPl / totalSaleFine) * 100, 1) : 0,
    stone_margin_pct: totalSaleStone ? round((stonePl / totalSaleStone) * 100, 1) : 0,
    has_missing_cost: false,
  };
}

// Per-item margin rows (Reports → Margins). Purchase cost is derived from the
// sale figures so the table always reconciles; a few rows are marked
// "missing"/"actual" to exercise the page's row-colour states.
function touchOf(item) {
  const map = { "24K": 99.9, "22K": 91.6, "18K": 75, "14K": 58.5 };
  return Number(item.touch || map[item.purity] || 91.6);
}
async function marginsRows() {
  const items = await list(STORES.inventory);
  return items
    .filter((i) => i.net_weight > 0)
    .map((i, idx) => {
      const saleTouch = touchOf(i);
      const saleNet = Number(i.net_weight || 0);
      const saleStone = Number(i.stone_charges || 0);
      // purchase slightly cheaper than sale → positive margin
      const purchaseTouch = round(saleTouch - 0.4, 2);
      const purchaseNet = round(saleNet * 1.004, 3); // tiny wastage on purchase
      const purchaseStone = Math.round(saleStone * 0.78);
      const mod = idx % 7;
      const purchase_source = mod === 0 ? "missing" : mod === 1 ? "estimated" : "actual";
      return {
        id: i.id,
        tag_no: i.tag_no,
        item_name: i.item_name,
        purity: i.purity,
        status: i.status,
        sale_net_weight: saleNet,
        sale_touch: saleTouch,
        sale_stone_charges: saleStone,
        purchase_net_weight: purchase_source === "missing" ? null : purchaseNet,
        purchase_touch: purchase_source === "missing" ? null : purchaseTouch,
        purchase_stone_charges: purchase_source === "missing" ? null : purchaseStone,
        net_weight_delta: round(saleNet - purchaseNet, 3),
        purchase_source: purchase_source === "missing" ? null : purchase_source,
      };
    });
}

// Per-sold-item profit/loss rows (Reports → Profit/Loss). Links each sold item
// back to the invoice/customer recorded by linkSoldItems().
async function profitLossRows() {
  const items = (await list(STORES.inventory)).filter((i) => i.status === "sold");
  return items.map((i, idx) => {
    const touch = touchOf(i);
    const saleFine = round(Number(i.net_weight || 0) * (touch / 100), 3);
    const purchaseFine = round(saleFine * 0.9, 3);
    const saleStone = Number(i.stone_charges || 0);
    const purchaseStone = Math.round(saleStone * 0.78);
    const cost_source = idx % 9 === 0 ? "missing" : idx % 3 === 0 ? "estimated" : "actual";
    return {
      estimate_no: i.sold_invoice_number || "",
      customer_name: i.sold_customer_name || "",
      tag_no: i.tag_no,
      item_name: i.item_name,
      sale_fine: saleFine,
      purchase_fine: cost_source === "missing" ? null : purchaseFine,
      metal_pl: cost_source === "missing" ? 0 : round(saleFine - purchaseFine, 3),
      stone_pl: cost_source === "missing" ? null : saleStone - purchaseStone,
      cost_source,
    };
  });
}

// Expense petty-cash wallet (Reports/Expenses). Derives header totals and the
// per-user balance cards from the seeded expense + funds + settlement rows.
async function expenseWallet() {
  const txns = await list(STORES.expenses);
  let credits = 0;
  let debits = 0;
  const byUser = {};
  txns.forEach((t) => {
    const amt = Number(t.amount || 0);
    const tt = String(t.transaction_type || t.type || "").toLowerCase();
    if (tt === "funds" || tt === "credit") credits += amt;
    else if (tt === "settlement") {
      // signed settlement; positive = paid to user, negative = returned
    } else debits += amt;
    const uid_ = String(t.user_account_id || "");
    if (!uid_) return;
    if (!byUser[uid_]) {
      byUser[uid_] = {
        user_account_id: uid_,
        display_name: t.expense_by || t.created_by_name || "User",
        expenses: 0,
        settlements: 0,
        balance: 0,
      };
    }
    if (tt === "settlement") byUser[uid_].settlements += amt;
    else if (tt === "expense" || tt === "debit") byUser[uid_].expenses += amt;
  });
  const user_summaries = Object.values(byUser).map((u) => ({
    ...u,
    expenses: round(u.expenses, 0),
    settlements: round(u.settlements, 0),
    // positive balance = user holds company cash they've yet to spend/settle
    balance: round(u.expenses - u.settlements - u.expenses * 0.82, 0),
  }));
  return {
    balance: {
      available_balance: round(credits - debits, 0),
      cash_added: round(credits, 0),
      credits: round(credits, 0),
      debits: round(debits, 0),
    },
    settings: { expense_user_mode: "manual" },
    user_summaries,
  };
}

async function ledgerActivity(params) {
  const [invoices, payments] = await Promise.all([list(STORES.estimates), list(STORES.payments)]);
  const rows = [];
  invoices
    .filter((i) => i.doc_type === "invoice")
    .forEach((inv) =>
      rows.push({
        date: inv.invoice_date,
        type: "sale",
        description: `Invoice ${inv.invoice_number}`,
        customer_name: inv.customer_name,
        amount: Number(inv.grand_total || 0),
      })
    );
  payments
    .filter((p) => p.party_type === "customer")
    .forEach((p) =>
      rows.push({
        date: p.date,
        type: "payment",
        description: p.note || `Receipt ${p.ref_no || ""}`.trim(),
        customer_name: p.party_name,
        amount: Number(p.cash_amount || 0),
      })
    );
  rows.sort((a, b) => new Date(b.date) - new Date(a.date));
  return rows.slice(0, Number(params?.limit || 250));
}

// ---------------------------------------------------------------- main router
// Returns { status, data }. `ctx` = { method, path, body, params }.
export async function handle(ctx) {
  const method = String(ctx.method || "get").toLowerCase();
  const path = ctx.path || "/";
  const body = ctx.body;
  const params = ctx.params || {};
  const isWrite = ["post", "put", "patch", "delete"].includes(method);

  // Hard block dangerous endpoints regardless of method.
  if (isBlocked(path)) return blocked();

  // ---- auth / boot ----
  if (path === "/auth/me") return ok(demoUser());
  if (path === "/auth/access-route") return ok({ blocked: false, allowed: true });
  if (path === "/auth/security-controls")
    return ok({ allow_screenshots: true, allow_screen_share: true, session_timeout_minutes: 0 });
  if (path === "/auth/heartbeat") return ok({ ok: true });
  if (path === "/auth/logout") return ok({ ok: true });
  if (path === "/auth/refresh") return ok({ access_token: "demo", refresh_token: "demo", session_id: "demo" });
  if (path === "/auth/security-activity/log") return ok({ ok: true });
  if (path === "/client-log") return ok({ ok: true });
  if (path === "/health" || path === "/") return ok({ status: "ok", demo: true });
  if (path.startsWith("/push/")) return ok({ ok: true });
  if (path.startsWith("/local-security/")) {
    // Demo is hosted (non-Electron); report "not configured" so the app never
    // shows the vault flow and never thinks the backend is mismatched.
    if (path === "/local-security/status") return ok({ configured: false, unlocked: true });
    return ok({ ok: true });
  }

  // ---- AI assistant (hybrid, capped) ----
  if (path.startsWith("/assistant/")) return handleAi(body);

  // ---- dashboard ----
  if (path === "/dashboard/stats") {
    const customers = await list(STORES.customers);
    return ok({ total_customers: customers.length });
  }
  if (path === "/dashboard/tasks" && method === "post") {
    const task = { id: uid("task"), _id: uid("task"), ...body, status: "pending", created_at: nowIso() };
    task._id = task.id;
    await put(STORES.dashboardTasks, task);
    return created(task);
  }
  if (path === "/dashboard/summary" || path === "/dashboard/quick") return ok(await dashboardSummary());
  if (path === "/dashboard/vision") return ok(await dashboardTasksForMonth(params.month));
  if (path === "/dashboard/tasks") return ok(await dashboardTasksForDate(params.date));
  if (path === "/dashboard/calendar-feed") return ok(await dashboardCalendarFeed(params.month));
  if (path === "/dashboard/notifications") return ok(await dashboardNotifications());
  if (path === "/dashboard/live-rates") return ok(await liveRates());
  if (path.startsWith("/dashboard/tasks/") && (method === "put" || method === "patch")) {
    const id = tail(path, "/dashboard/tasks");
    const existing = (await getById(STORES.dashboardTasks, id)) || { id };
    const updated = { ...existing, ...body, id, updated_at: nowIso() };
    await put(STORES.dashboardTasks, updated);
    return ok(updated);
  }
  if (path.startsWith("/dashboard/tasks/") && method === "delete") {
    await remove(STORES.dashboardTasks, tail(path, "/dashboard/tasks"));
    return ok({ ok: true });
  }

  // ---- purchases ----
  if (path === "/purchases/list") return ok(await list(STORES.purchases));
  if (path === "/purchases/bootstrap") {
    const [purchases, suppliers, gold] = await Promise.all([
      list(STORES.purchases),
      list(STORES.suppliers),
      list(STORES.goldRates),
    ]);
    const out = { purchases, suppliers, gold_rate_latest: gold[0] || {} };
    if (params && params.include_item_master) out.item_master = await list(STORES.inventory);
    return ok(out);
  }
  if (path === "/gold-rate/latest") return ok((await list(STORES.goldRates))[0] || {});
  if (path === "/items/next-tag-no") {
    const items = await list(STORES.inventory);
    const maxTag = items.reduce((m, i) => Math.max(m, Number(i.tag_no) || 0), 1000);
    return ok({ next_tag_no: String(maxTag + 1) });
  }
  if (path === "/item-codes") return method === "post" ? ok({ ok: true }) : ok([]);
  if (path === "/purchases") {
    if (method === "post") {
      const seq = (await list(STORES.purchases)).length + 4133;
      const row = {
        id: uid("pur"),
        purchase_number: body?.purchase_number || `PUR-${seq}`,
        status: "pending",
        created_at: nowIso(),
        purchase_date: body?.purchase_date || new Date().toISOString().slice(0, 10),
        ...body,
      };
      await put(STORES.purchases, row);
      return created(row);
    }
    return ok(await list(STORES.purchases));
  }
  if (path.startsWith("/purchases/")) {
    const seg = tail(path, "/purchases");
    const id = seg.split("/")[0];
    if (seg.includes("/")) return isWrite ? ok({ ok: true }) : ok({}); // settle / rate-fix / manual-derived-details
    if (method === "put" || method === "patch") {
      const existing = (await getById(STORES.purchases, id)) || { id };
      const updated = { ...existing, ...body, id, updated_at: nowIso() };
      await put(STORES.purchases, updated);
      return ok(updated);
    }
    if (method === "delete") {
      await remove(STORES.purchases, id);
      return ok({ ok: true });
    }
    return ok((await getById(STORES.purchases, id)) || {});
  }

  // ---- suppliers (balance views) ----
  if (path === "/suppliers/bootstrap") {
    const [suppliers, pending] = await Promise.all([list(STORES.suppliers), supplierBootstrapPending()]);
    return ok({ suppliers: suppliers.map(withAltId), pending_suppliers: pending });
  }
  if (path === "/suppliers/payments") {
    if (method === "post") {
      const row = { id: uid("pay"), party_type: "supplier", created_at: nowIso(), ...body };
      await put(STORES.payments, row);
      return created(row);
    }
    return ok((await list(STORES.payments)).filter((p) => p.party_type === "supplier"));
  }
  if (path.startsWith("/suppliers/payments/")) return isWrite ? ok({ ok: true }) : ok({});

  // ---- customers (balance views) ----
  if (path === "/customers/paged") {
    const customers = await list(STORES.customers);
    const q = params?.search ? String(params.search).toLowerCase() : "";
    const rows = q
      ? customers.filter(
          (c) => String(c.name).toLowerCase().includes(q) || String(c.whatsapp || "").includes(q)
        )
      : customers;
    return ok({ items: rows.map(withAltId), has_more: false, next_cursor: null });
  }
  if (path === "/customers/pending-balances") {
    const customers = await list(STORES.customers);
    return ok(
      customers
        .filter((c) => Number(c.pending_cash || 0) > 0 || Number(c.pending_metal || 0) > 0)
        .map((c) => ({
          customer_id: c.id,
          customer_name: c.name,
          name: c.name,
          whatsapp: c.whatsapp,
          pending_cash: Number(c.pending_cash || 0),
          pending_metal: Number(c.pending_metal || 0),
        }))
    );
  }

  // ---- payments / ledgers / metal-storage / adjustments ----
  if (path === "/payments") {
    if (method === "post") {
      const row = { id: uid("pay"), party_type: "customer", created_at: nowIso(), ...body };
      await put(STORES.payments, row);
      return created(row);
    }
    return ok(await list(STORES.payments));
  }
  if (path.startsWith("/payments/")) return isWrite ? ok({ ok: true }) : ok([]);
  if (path === "/customer-metal-storage" || path.startsWith("/customer-metal-storage/"))
    return isWrite ? ok({ ok: true }) : ok([]);
  if (path.startsWith("/customer-ledger")) return isWrite ? ok({ ok: true }) : ok({});
  if (path === "/customer-balance-adjustments" || path.startsWith("/customer-balance-adjustments/"))
    return isWrite ? ok({ ok: true }) : ok([]);
  if (path === "/supplier-metal-storage" || path.startsWith("/supplier-metal-storage/"))
    return isWrite ? ok({ ok: true }) : ok([]);
  if (path.startsWith("/supplier-ledger")) return isWrite ? ok({ ok: true }) : ok({});
  if (path === "/supplier-balance-adjustments" || path.startsWith("/supplier-balance-adjustments/"))
    return isWrite ? ok({ ok: true }) : ok([]);

  // ---- karigars ----
  if (path === "/karigars") {
    if (method === "post") {
      const row = { id: uid("kar"), created_at: nowIso(), ...body };
      await put(STORES.karigars, row);
      return created(row);
    }
    return ok(await list(STORES.karigars));
  }
  if (path.startsWith("/karigars/")) {
    const seg = tail(path, "/karigars");
    const id = seg.split("/")[0];
    if (seg.endsWith("/overview")) return ok(await karigarOverview(id));
    if (seg.includes("/")) return isWrite ? ok({ ok: true }) : ok([]); // consumables / transactions / metal-defaults
    if (method === "put" || method === "patch") {
      const existing = (await getById(STORES.karigars, id)) || { id };
      const updated = { ...existing, ...body, id };
      await put(STORES.karigars, updated);
      return ok(updated);
    }
    if (method === "delete") {
      await remove(STORES.karigars, id);
      return ok({ ok: true });
    }
    return ok((await getById(STORES.karigars, id)) || {});
  }
  if (path === "/karigar-transactions" || path.startsWith("/karigar-transactions/"))
    return isWrite ? ok({ ok: true }) : ok([]);
  if (path === "/karigar-consumables" || path.startsWith("/karigar-consumables/"))
    return isWrite ? ok({ ok: true }) : ok([]);

  // ---- consignment / approval ----
  if (path === "/consignment/dashboard") return ok(await consignmentDashboard());
  if (path === "/consignment/suppliers/exposure") return ok(await supplierExposure());
  if (path.startsWith("/consignment/supplier/") && path.endsWith("/summary")) {
    const id = path.split("/")[3];
    const e = (await supplierExposure()).find((x) => x.supplier_id === id) || {};
    return ok({
      pending_approval_payable: e.exposure || 0,
      active_fine_weight: e.active_fine_weight || 0,
      memo_count: e.memo_count || 0,
    });
  }
  if (path === "/consignment/memos") {
    if (method === "post") {
      const seq = (await list(STORES.consignmentMemos)).length + 710;
      const row = {
        id: uid("memo"),
        memo_number: body?.memo_number || `APR-${seq}`,
        status: "active",
        created_at: nowIso(),
        received_date: body?.received_date || new Date().toISOString().slice(0, 10),
        items: [],
        ...body,
      };
      await put(STORES.consignmentMemos, row);
      return created(row);
    }
    let memos = await list(STORES.consignmentMemos);
    if (params?.status && params.status !== "all") memos = memos.filter((m) => m.status === params.status);
    if (params?.supplier_id) memos = memos.filter((m) => m.supplier_id === params.supplier_id);
    if (params?.search) {
      const q = String(params.search).toLowerCase();
      memos = memos.filter(
        (m) =>
          String(m.memo_number).toLowerCase().includes(q) ||
          String(m.supplier_name).toLowerCase().includes(q)
      );
    }
    return ok(memos);
  }
  if (path.startsWith("/consignment/memos/")) {
    const seg = tail(path, "/consignment/memos");
    const id = seg.split("/")[0];
    if (method === "delete") {
      await remove(STORES.consignmentMemos, id);
      return ok({ ok: true });
    }
    if (isWrite) return ok({ ok: true });
    const memo = (await getById(STORES.consignmentMemos, id)) || {};
    if (seg.endsWith("/settlement-preview"))
      return ok({
        payable: memo.estimated_payable || 0,
        fine_weight: memo.total_sold_weight || 0,
        interest: memo.interest_preview || 0,
      });
    if (seg.endsWith("/interest-preview")) return ok({ interest: memo.interest_preview || 0 });
    return ok(memo);
  }
  if (path.startsWith("/consignment/")) return isWrite ? ok({ ok: true }) : ok([]);

  // ---- expenses (petty-cash wallet) ----
  if (path === "/expense-wallet/add-expense" && method === "post") {
    const row = {
      id: uid("exp"),
      transaction_type: "expense",
      type: "debit",
      date: nowIso().slice(0, 10),
      created_at: nowIso(),
      status: "approved",
      ...(body || {}),
      amount: Number(body?.amount || 0),
    };
    await put(STORES.expenses, row);
    return created(row);
  }
  if (path === "/expense-wallet/add-funds" && method === "post") {
    const row = {
      id: uid("exp"),
      transaction_type: "funds",
      type: "credit",
      date: nowIso().slice(0, 10),
      created_at: nowIso(),
      status: "approved",
      category: "Funds",
      title: "Petty cash top-up",
      ...(body || {}),
      amount: Number(body?.amount || 0),
    };
    await put(STORES.expenses, row);
    return created(row);
  }
  if (path === "/expense-wallet") return ok(await expenseWallet());
  if (path === "/expense-wallet/history" || path === "/expense-wallet/transactions")
    return ok({ transactions: await list(STORES.expenses) });
  if (path === "/expense-wallet/users") {
    const wallet = await expenseWallet();
    return ok(
      wallet.user_summaries.map((u) => ({
        id: u.user_account_id,
        full_name: u.display_name,
        email: "",
        balance: u.balance,
      }))
    );
  }
  if (path.startsWith("/expense-wallet/transactions/") && method === "delete") {
    await remove(STORES.expenses, tail(path, "/expense-wallet/transactions"));
    return ok({ ok: true });
  }
  if (path.startsWith("/expense-wallet/")) return isWrite ? ok({ ok: true }) : ok([]);

  // ---- bulk inventory (weight/qty buckets) ----
  if (path === "/bulk-inventory/buckets") {
    if (method === "post") {
      const row = {
        id: uid("bkt"),
        balance: 0,
        allow_negative: false,
        created_at: nowIso(),
        ...(body || {}),
      };
      await put(STORES.bulkBuckets, row);
      return created(row);
    }
    return ok(await list(STORES.bulkBuckets));
  }
  if (path === "/bulk-inventory/ledger" && method === "post") {
    const qty = Number(body?.quantity || 0);
    const row = { id: uid("blg"), txn_date: nowIso().slice(0, 10), created_at: nowIso(), ...(body || {}), quantity: qty };
    await put(STORES.bulkLedger, row);
    // keep the bucket balance consistent
    if (body?.bucket_id) {
      const b = await getById(STORES.bulkBuckets, body.bucket_id);
      if (b) {
        b.balance = round(Number(b.balance || 0) + qty, 3);
        await put(STORES.bulkBuckets, b);
      }
    }
    return created(row);
  }
  if (path.startsWith("/bulk-inventory/buckets/") && path.endsWith("/ledger")) {
    const id = path.split("/")[3];
    const rows = (await list(STORES.bulkLedger))
      .filter((e) => e.bucket_id === id)
      .sort((a, b) => new Date(b.txn_date || 0) - new Date(a.txn_date || 0));
    return ok(rows);
  }
  if (path.startsWith("/bulk-inventory/")) return isWrite ? ok({ ok: true }) : ok([]);

  // ---- purchase returns (standalone) ----
  if (path === "/purchase-returns") {
    if (method === "post") {
      const seq = (await list(STORES.purchaseReturns)).length + 922;
      const row = {
        id: uid("pret"),
        return_no: body?.return_no || `PRTN-${seq}`,
        return_number: body?.return_no || `PRTN-${seq}`,
        status: "completed",
        date: body?.date || nowIso().slice(0, 10),
        created_at: nowIso(),
        ...(body || {}),
      };
      await put(STORES.purchaseReturns, row);
      return created(row);
    }
    return ok(await list(STORES.purchaseReturns));
  }
  if (path.startsWith("/purchase-returns/")) {
    const id = tail(path, "/purchase-returns");
    if (method === "delete") {
      await remove(STORES.purchaseReturns, id);
      return ok({ ok: true });
    }
    if (isWrite) return ok({ ok: true });
    return ok((await getById(STORES.purchaseReturns, id)) || {});
  }

  // ---- pipeline ----
  if (path === "/pipeline/board") {
    const leads = await list(STORES.pipelineLeads);
    const stages = ["new", "contacted", "quoted", "negotiation", "won", "lost"];
    return ok({ stages, columns: stages.map((st) => ({ stage: st, leads: leads.filter((l) => l.stage === st) })) });
  }
  if (path === "/pipeline/leads") {
    if (method === "post") {
      const row = { id: uid("lead"), stage: "new", created_at: nowIso(), ...body };
      await put(STORES.pipelineLeads, row);
      return created(row);
    }
    return ok(await list(STORES.pipelineLeads));
  }
  if (path.startsWith("/pipeline/leads/")) {
    const id = tail(path, "/pipeline/leads").split("/")[0];
    if (method === "delete") {
      await remove(STORES.pipelineLeads, id);
      return ok({ ok: true });
    }
    if (isWrite) {
      const existing = (await getById(STORES.pipelineLeads, id)) || { id };
      const updated = { ...existing, ...body, id };
      await put(STORES.pipelineLeads, updated);
      return ok(updated);
    }
    return ok((await getById(STORES.pipelineLeads, id)) || {});
  }

  // ---- gold rate / settings ----
  if (path === "/gold-rate" || path === "/settings/gold-rate") {
    if (method === "post" || method === "put") {
      const cur = (await list(STORES.goldRates))[0] || { id: "current" };
      const updated = { ...cur, ...body, id: "current", updated_at: nowIso(), date: body?.date || cur.date };
      await put(STORES.goldRates, updated);
      return ok(updated);
    }
    return ok((await list(STORES.goldRates))[0] || {});
  }
  if (path === "/settings/metal-rates") {
    if (method === "put" || method === "post") return ok({ ok: true, ...(body || {}) });
    const r = (await list(STORES.goldRates))[0] || {};
    return ok({ rate_24k: r.rate_24k, rate_22k: r.rate_22k, rate_18k: r.rate_18k, rate_14k: r.rate_14k });
  }
  if (path === "/item-master/settings" || path.startsWith("/settings/")) {
    return isWrite ? ok({ ok: true }) : ok({});
  }

  // ---- customers ----
  if (path === "/customers") {
    if (method === "post") {
      if (!canCreate("customers")) return blocked(limitMessage("customers"));
      recordCreate("customers");
      const row = { id: uid("cus"), ...body, is_seed: false, created_at: nowIso(), metadata_json: "{}" };
      await put(STORES.customers, row);
      return created(row);
    }
    return ok(await list(STORES.customers));
  }
  if (path.startsWith("/customers/")) {
    const id = tail(path, "/customers");
    if (method === "put" || method === "patch") {
      const existing = (await getById(STORES.customers, id)) || { id };
      const updated = { ...existing, ...body, id, updated_at: nowIso() };
      await put(STORES.customers, updated);
      return ok(updated);
    }
    if (method === "delete") {
      await remove(STORES.customers, id);
      return ok({ ok: true });
    }
    return ok((await getById(STORES.customers, id)) || {});
  }

  // ---- suppliers ----
  if (path === "/suppliers") {
    if (method === "post") {
      const row = { id: uid("sup"), ...body, is_seed: false, created_at: nowIso(), metadata_json: "{}" };
      await put(STORES.suppliers, row);
      return created(row);
    }
    return ok(await list(STORES.suppliers));
  }
  if (path.startsWith("/suppliers/")) {
    const id = tail(path, "/suppliers");
    if (method === "put" || method === "patch") {
      const existing = (await getById(STORES.suppliers, id)) || { id };
      const updated = { ...existing, ...body, id };
      await put(STORES.suppliers, updated);
      return ok(updated);
    }
    if (method === "delete") {
      await remove(STORES.suppliers, id);
      return ok({ ok: true });
    }
    return ok((await getById(STORES.suppliers, id)) || {});
  }

  // ---- items / inventory ----
  if (path === "/items/bootstrap") return ok(await itemsBootstrap(params));
  if (path === "/items/paged") {
    const rows = filterItems(await list(STORES.inventory), params);
    return ok({ items: rows, has_more: false, next_cursor: null, total: rows.length });
  }
  if (path === "/items/scan-review") {
    const raw = body?.parsed_fields || body?.rowData || body?.raw_scan_value || body || {};
    const confidenceScore = raw?.tag_no ? 0.99 : 0.25;
    const confidenceBand = raw?.tag_no ? "high" : "low";
    return ok({
      status: confidenceBand === "high" ? "matched" : "review",
      confidence_band: confidenceBand,
      confidence_score: confidenceScore,
      selected_candidate: raw?.tag_no
        ? {
            id: "demo-scan-template",
            template_name: "Demo Item Scan",
            confidence_band: confidenceBand,
          }
        : null,
      candidates: raw?.tag_no
        ? [
            {
              id: "demo-scan-template",
              template_name: "Demo Item Scan",
              confidence_band: confidenceBand,
            },
          ]
        : [],
      boundary_mode: confidenceBand,
    });
  }
  if (path === "/items") {
    if (method === "post") {
      const row = { id: uid("itm"), status: "available", created_at: nowIso(), metadata_json: "{}", ...body };
      await put(STORES.inventory, row);
      return created(row);
    }
    return ok(filterItems(await list(STORES.inventory), params));
  }
  if (path.startsWith("/items/")) {
    // create-from-template / bulk endpoints → benign success
    if (method === "post") return ok({ ok: true });
    const id = tail(path, "/items");
    if (method === "put" || method === "patch") {
      const existing = (await getById(STORES.inventory, id)) || { id };
      const updated = { ...existing, ...body, id, updated_at: nowIso() };
      await put(STORES.inventory, updated);
      return ok(updated);
    }
    if (method === "delete") {
      await remove(STORES.inventory, id);
      return ok({ ok: true });
    }
    return ok((await getById(STORES.inventory, id)) || {});
  }

  // ---- invoices / estimates ----
  if (path === "/invoices/paged") {
    let rows = await list(STORES.estimates);
    if (params?.status) rows = rows.filter((r) => String(r.status) === String(params.status));
    if (params?.search) {
      const q = String(params.search).toLowerCase();
      rows = rows.filter(
        (r) =>
          String(r.invoice_number || "").toLowerCase().includes(q) ||
          String(r.customer_name || "").toLowerCase().includes(q)
      );
    }
    if (params?.from_date) rows = rows.filter((r) => String(r.invoice_date) >= String(params.from_date));
    if (params?.to_date) rows = rows.filter((r) => String(r.invoice_date) <= String(params.to_date));
    rows = rows
      .slice()
      .sort((a, b) => new Date(b.invoice_date || 0) - new Date(a.invoice_date || 0));
    return ok({ items: rows, has_more: false, next_cursor: null, total: rows.length });
  }
  if (path === "/invoices/bootstrap") {
    const customers = await list(STORES.customers);
    const enabled_tabs = [
      "gold_jewellery",
      "silver_jewellery",
      "platinum_jewellery",
      "diamond_jewellery",
      "lab_diamond_jewellery",
      "gold_bullion",
      "silver_bullion",
      "stones",
    ];
    const out = {
      customers,
      item_master_settings: { enabled_tabs, enable_hsn_sac: false, headers_by_tab: {} },
      calculation_modes: {},
    };
    if (params && (params.include_items === true || params.include_items === "true")) {
      out.items = await list(STORES.inventory);
    }
    return ok(out);
  }
  if (path === "/invoices") {
    if (method === "post") {
      if (!canCreate("estimates")) return blocked(limitMessage("estimates"));
      recordCreate("estimates");
      const seq = (await list(STORES.estimates)).length + 2407;
      const row = {
        id: uid("inv"),
        invoice_number: body?.invoice_number || `EST-${seq}`,
        status: "pending",
        created_at: nowIso(),
        invoice_date: body?.invoice_date || new Date().toISOString().slice(0, 10),
        ...body,
      };
      await put(STORES.estimates, row);
      return created(row);
    }
    return ok(await list(STORES.estimates));
  }
  if (path.startsWith("/invoices/")) {
    const id = tail(path, "/invoices");
    if (method === "put" || method === "patch") {
      const existing = (await getById(STORES.estimates, id)) || { id };
      const updated = { ...existing, ...body, id, updated_at: nowIso() };
      await put(STORES.estimates, updated);
      return ok(updated);
    }
    if (method === "delete") {
      await remove(STORES.estimates, id);
      return ok({ ok: true });
    }
    return ok((await getById(STORES.estimates, id)) || {});
  }

  // ---- reports (analytics) ----
  if (path === "/reports/customers-pending-summary/paged") {
    const rows = await customerPendingRows();
    const q = params?.search ? String(params.search).toLowerCase() : "";
    const items = q
      ? rows.filter(
          (r) => r.customer.name.toLowerCase().includes(q) || String(r.customer.whatsapp || "").includes(q)
        )
      : rows;
    return ok({ items, has_more: false, next_cursor: null });
  }
  if (path === "/reports/customers-pending-summary") return ok(await customerPendingRows());
  if (path === "/reports/suppliers-pending-summary") return ok(await supplierPendingSummary());
  if (path === "/reports/customer-statement") return ok(await customerStatement(params?.customer_id));
  if (path === "/reports/supplier-statement") return ok(await supplierStatement(params?.supplier_id));
  if (path === "/reports/stock-summary") return ok(await stockSummary());
  if (path === "/reports/profit-loss/overview") return ok(await pnlOverview());
  if (path === "/reports/profit-loss") return ok(await profitLossRows());
  if (path === "/reports/margins") return ok(await marginsRows());
  if (path === "/reports/repairs") {
    let rows = await list(STORES.repairs);
    if (params?.status) rows = rows.filter((r) => String(r.status) === String(params.status));
    if (params?.customer_id) rows = rows.filter((r) => r.customer_id === params.customer_id);
    return ok(
      rows.map((r) => ({
        ...r,
        pending_amount: Math.max(0, Number(r.amount || 0) - Number(r.advance || 0)),
      }))
    );
  }
  if (path === "/reports/returns") {
    const returns = await list(STORES.purchaseReturns);
    return ok(
      returns.map((r) => ({
        id: r.id,
        date: r.date,
        return_no: r.return_no,
        supplier_name: r.supplier_name,
        linked_purchase_number: r.linked_purchase_number,
        item_name: (r.tags && r.tags[0]) || "",
        reason: r.reason,
        weight: r.fine_weight,
        fine_weight: r.fine_weight,
        amount: r.total_amount,
        total_amount: r.total_amount,
        status: r.status,
      }))
    );
  }
  if (path === "/reports/ledger-activity") return ok(await ledgerActivity(params));
  if (path === "/reports/estimates") {
    const invoices = await list(STORES.estimates);
    return ok(
      invoices.map((inv) => ({
        date: inv.invoice_date,
        invoice_date: inv.invoice_date,
        invoice_number: inv.invoice_number,
        customer_name: inv.customer_name,
        subtotal: inv.subtotal,
        total_without_gst: inv.subtotal,
        grand_total: inv.grand_total,
        total_fine: inv.total_fine,
        fine_sold_g: inv.total_fine,
        invoice_metal_component: inv.invoice_metal_component,
      }))
    );
  }
  if (path.startsWith("/reports/")) return ok([]);

  // ---- repairs ----
  if (path === "/repairs") {
    if (method === "post") {
      if (!canCreate("repairs")) return blocked(limitMessage("repairs"));
      recordCreate("repairs");
      const seq = (await list(STORES.repairs)).length + 1046;
      const row = {
        id: uid("rpr"),
        job_no: body?.job_no || `RPR-${seq}`,
        status: "pending",
        created_at: nowIso(),
        estimate_date: body?.estimate_date || new Date().toISOString().slice(0, 10),
        items_json: "[]",
        photos_json: "[]",
        ...body,
      };
      await put(STORES.repairs, row);
      return created(row);
    }
    return ok(await list(STORES.repairs));
  }
  if (path.startsWith("/repairs/")) {
    const id = tail(path, "/repairs");
    if (method === "put" || method === "patch") {
      const existing = (await getById(STORES.repairs, id)) || { id };
      const updated = { ...existing, ...body, id, updated_at: nowIso() };
      await put(STORES.repairs, updated);
      return ok(updated);
    }
    if (method === "delete") {
      await remove(STORES.repairs, id);
      return ok({ ok: true });
    }
    return ok((await getById(STORES.repairs, id)) || {});
  }

  // ---- orders (work orders) ----
  if (path === "/karigar-orders") {
    if (method === "post") {
      if (!canCreate("orders")) return blocked(limitMessage("orders"));
      recordCreate("orders");
      const seq = (await list(STORES.orders)).length + 2211;
      const row = {
        id: uid("ord"),
        work_order_no: body?.work_order_no || `ORD-${seq}`,
        status: "pending",
        created_at: nowIso(),
        order_date: body?.order_date || new Date().toISOString().slice(0, 10),
        ...body,
      };
      await put(STORES.orders, row);
      return created(row);
    }
    return ok(await list(STORES.orders));
  }
  if (path.startsWith("/karigar-orders/")) {
    const id = tail(path, "/karigar-orders");
    if (method === "put" || method === "patch") {
      const existing = (await getById(STORES.orders, id)) || { id };
      const updated = { ...existing, ...body, id, updated_at: nowIso() };
      await put(STORES.orders, updated);
      return ok(updated);
    }
    if (method === "delete") {
      await remove(STORES.orders, id);
      return ok({ ok: true });
    }
    return ok((await getById(STORES.orders, id)) || {});
  }

  // ---- everything else: safe defaults ----
  // Unknown writes succeed benignly; unknown reads return an empty collection.
  // (These keep secondary pages from erroring; enrich with real handlers later.)
  return isWrite ? ok({ ok: true }) : ok([]);
}

// Path prefixes/exact routes the demo EXPLICITLY handles. Used by the build-time
// coverage check (scripts/check-demo-coverage.js) to flag new frontend endpoints
// that would silently fall through to the generic default above.
export const EXPLICIT_ROUTES = [
  "/auth/me",
  "/auth/access-route",
  "/auth/security-controls",
  "/auth/heartbeat",
  "/auth/logout",
  "/auth/refresh",
  "/auth/security-activity/log",
  "/client-log",
  "/health",
  "/push/",
  "/local-security/",
  "/assistant/",
  "/dashboard/stats",
  "/dashboard/summary",
  "/dashboard/quick",
  "/dashboard/vision",
  "/dashboard/live-rates",
  "/dashboard/tasks",
  "/dashboard/calendar-feed",
  "/dashboard/notifications",
  "/gold-rate",
  "/settings/gold-rate",
  "/settings/metal-rates",
  "/settings/",
  "/item-master/settings",
  "/customers",
  "/suppliers",
  "/items",
  "/items/bootstrap",
  "/items/paged",
  "/items/scan-review",
  "/invoices",
  "/invoices/paged",
  "/invoices/bootstrap",
  "/reports/estimates",
  "/reports/margins",
  "/reports/profit-loss",
  "/reports/profit-loss/overview",
  "/reports/repairs",
  "/reports/returns",
  "/reports/",
  "/repairs",
  "/karigar-orders",
  // back-office (linked demo stores)
  "/purchases",
  "/purchases/list",
  "/purchases/bootstrap",
  "/gold-rate/latest",
  "/items/next-tag-no",
  "/item-codes",
  "/suppliers/bootstrap",
  "/suppliers/payments",
  "/customers/paged",
  "/customers/pending-balances",
  "/payments",
  "/customer-metal-storage",
  "/customer-ledger",
  "/customer-balance-adjustments",
  "/supplier-metal-storage",
  "/supplier-ledger",
  "/supplier-balance-adjustments",
  "/karigars",
  "/karigar-transactions",
  "/karigar-consumables",
  "/consignment",
  "/expense-wallet",
  "/bulk-inventory",
  "/purchase-returns",
  "/pipeline",
];

// True if `path` is matched by an explicit route (prefix or exact).
export function isExplicitlyHandled(path) {
  if (isBlocked(path)) return true;
  return EXPLICIT_ROUTES.some((r) => (r.endsWith("/") ? path.startsWith(r) : path === r || path.startsWith(r + "/")));
}
